/**
 * @fileoverview Enhanced Purchase Confirmation Component
 * 
 * This component displays a confirmation modal for phone number purchases
 * with pricing breakdown, billing validation, and purchase details.
 */

import React, { useMemo, useState, useEffect } from 'react';
import { PhoneNumberContext } from '../types.js';
import { formatPhoneNumber, calculateTotalCost } from '../utils/helpers.js';
import { 
  validatePurchaseEligibility, 
  formatValidationErrors, 
  generateConfirmationMessage 
} from '../utils/billingValidation.js';

/**
 * Enhanced Purchase Confirmation Modal Component
 * 
 * @param {Object} props - Component props
 * @param {PhoneNumber} props.number - Phone number to purchase
 * @param {PhoneNumberConfig} props.config - Module configuration
 * @param {function} props.onConfirm - Callback for purchase confirmation
 * @param {function} props.onCancel - Callback for purchase cancellation
 * @param {boolean} props.isPurchasing - Whether purchase is in progress
 * @returns {React.ReactElement} Purchase confirmation modal
 */
const PurchaseConfirmationEnhanced = ({
  number,
  config,
  onConfirm,
  onCancel,
  isPurchasing
}) => {
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(true);

  // Validate purchase eligibility
  useEffect(() => {
    if (config.billingContext && number) {
      setIsValidating(true);
      
      // Simulate async validation (in real app, this might be an API call)
      setTimeout(() => {
        const result = validatePurchaseEligibility(config.billingContext, number);
        setValidationResult(result);
        setIsValidating(false);
      }, 100);
    } else {
      // Fallback to basic cost calculation if no billing context
      const basicCostInfo = calculateTotalCost(number);
      setValidationResult({
        canPurchase: true,
        warnings: [],
        errors: [],
        costInfo: basicCostInfo,
        requiresPayment: basicCostInfo.total > 0
      });
      setIsValidating(false);
    }
  }, [config.billingContext, number]);

  const costInfo = useMemo(() => {
    if (validationResult?.costInfo) {
      return validationResult.costInfo;
    }
    return calculateTotalCost(number);
  }, [validationResult, number]);

  const formattedErrors = useMemo(() => {
    if (!validationResult) return null;
    return formatValidationErrors(validationResult);
  }, [validationResult]);

  const confirmationMessage = useMemo(() => {
    if (!validationResult) return '';
    return generateConfirmationMessage(validationResult, number);
  }, [validationResult, number]);

  // Handle purchase confirmation with validation
  const handleConfirmPurchase = () => {
    if (validationResult?.canPurchase) {
      onConfirm();
    }
  };

  if (isValidating) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full mx-4 shadow-xl dark:bg-gray-800">
          <div className="px-6 py-8 text-center">
            <div className="animate-spin h-8 w-8 border-4 border-teal-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-300 dark:text-gray-600">Validating purchase eligibility...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full mx-4 shadow-xl dark:bg-gray-800">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Confirm Phone Number Purchase
          </h3>
          <p className="text-sm text-gray-600 mt-1 dark:text-gray-300 dark:text-gray-600">
            {confirmationMessage || 'Please review the details before completing your purchase'}
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {/* Validation Errors */}
          {formattedErrors?.hasErrors && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div>
                  <h4 className="text-red-800 font-medium mb-1">Purchase Not Available</h4>
                  <div className="text-red-700 text-sm">
                    {formattedErrors.allErrors.map((error, index) => (
                      <div key={index} className="mb-1">• {error}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Validation Warnings */}
          {formattedErrors?.hasWarnings && !formattedErrors?.hasErrors && (
            <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-yellow-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <h4 className="text-yellow-800 font-medium mb-1">Important Notice</h4>
                  <div className="text-yellow-700 text-sm">
                    {formattedErrors.allWarnings.map((warning, index) => (
                      <div key={index} className="mb-1">• {warning}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Phone Number Details */}
          <div className="mb-6">
            <div className="text-center mb-4">
              <div className="font-mono text-2xl font-bold text-gray-900 dark:text-white">
                {formatPhoneNumber(number.phoneNumber)}
              </div>
              <div className="text-sm text-gray-600 mt-1 dark:text-gray-300 dark:text-gray-600">
                {number.locality && `${number.locality}, `}
                {number.region && `${number.region}, `}
                {number.country}
              </div>
            </div>

            {/* Capabilities */}
            {number.capabilities && (
              <div className="flex justify-center space-x-2 mb-4">
                {number.capabilities.voice && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                    </svg>
                    Voice Calls
                  </span>
                )}
                {number.capabilities.sms && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                    </svg>
                    SMS Messages
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Enhanced Pricing Breakdown */}
          {(number.pricing || validationResult?.costInfo) && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 dark:bg-gray-900">
              <h4 className="font-medium text-gray-900 mb-3 dark:text-white">Pricing Details</h4>
              
              <div className="space-y-2 text-sm">
                {/* Setup Fee */}
                {costInfo.setupFee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300 dark:text-gray-600">One-time setup fee:</span>
                    <span className="font-medium">${costInfo.setupFee.toFixed(2)}</span>
                  </div>
                )}

                {/* Monthly Fee */}
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300 dark:text-gray-600">Monthly cost:</span>
                  <span className="font-medium">
                    {costInfo.isIncludedInPlan || costInfo.isIncluded ? (
                      <span className="text-green-600">Included in Plan</span>
                    ) : costInfo.isMonthlyIncluded ? (
                      <span className="text-green-600">No Monthly Fee</span>
                    ) : (
                      `$${costInfo.monthlyFee.toFixed(2)}/month`
                    )}
                  </span>
                </div>

                {/* Plan Usage Information */}
                {config.billingContext && (
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">
                      Plan usage: {config.billingContext.phoneNumbersUsed || 0} of {config.billingContext.phoneNumbersIncluded || 0} included
                    </span>
                  </div>
                )}

                {/* Total for first month */}
                {costInfo.total > 0 && (
                  <>
                    <div className="border-t border-gray-200 pt-2 mt-2 dark:border-gray-700">
                      <div className="flex justify-between font-medium">
                        <span className="text-gray-900 dark:text-white">Total due today:</span>
                        <span className={`text-gray-900 ${costInfo.isIncludedInPlan ? 'text-green-600' : ''}`}>
                          {costInfo.isIncludedInPlan ? 'FREE' : `$${costInfo.total.toFixed(2)}`}
                        </span>
                      </div>
                    </div>
                    {!costInfo.isIncludedInPlan && (
                      <div className="text-xs text-gray-500">
                        {costInfo.setupFee > 0 && costInfo.monthlyFee > 0 
                          ? 'Includes setup fee + first month'
                          : costInfo.setupFee > 0 
                            ? 'One-time setup fee'
                            : 'First month charge'
                        }
                      </div>
                    )}
                  </>
                )}

                {/* Free number indicator */}
                {costInfo.total === 0 && (
                  <div className="border-t border-gray-200 pt-2 mt-2 dark:border-gray-700">
                    <div className="flex justify-between font-medium">
                      <span className="text-gray-900 dark:text-white">Total due today:</span>
                      <span className="text-green-600 font-semibold">FREE</span>
                    </div>
                    <div className="text-xs text-green-600 mt-1">
                      {costInfo.isIncludedInPlan ? 'Included in your plan' : 'No charges for this number'}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Enhanced Billing Context Information */}
          {config.billingContext && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
              <div className="text-sm">
                <div className="font-medium text-blue-900 mb-1">Account Information</div>
                <div className="text-blue-800 space-y-1">
                  {config.billingContext.phoneNumbersIncluded > 0 && (
                    <div>
                      Plan includes: {config.billingContext.phoneNumbersIncluded} phone number(s)
                    </div>
                  )}
                  {config.billingContext.phoneNumbersUsed !== undefined && (
                    <div>
                      Currently using: {config.billingContext.phoneNumbersUsed} number(s)
                    </div>
                  )}
                  {config.billingContext.walletBalance !== undefined && validationResult?.requiresPayment && (
                    <div>
                      Wallet balance: ${(config.billingContext.walletBalance / 100).toFixed(2)}
                    </div>
                  )}
                  {config.billingContext.additionalNumberCost && config.billingContext.phoneNumbersUsed >= config.billingContext.phoneNumbersIncluded && (
                    <div>
                      Additional number rate: ${(config.billingContext.additionalNumberCost.amount / 100).toFixed(2)}/month
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Context-specific messaging */}
          {config.context === PhoneNumberContext.ONBOARDING && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6">
              <div className="text-sm text-green-800">
                <div className="font-medium mb-1">Welcome to ParrotMate!</div>
                <div>
                  This number will be assigned to your first Parrot and ready to receive calls immediately after purchase.
                </div>
              </div>
            </div>
          )}

          {config.context === PhoneNumberContext.ADMIN && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
              <div className="text-sm text-yellow-800">
                <div className="font-medium mb-1">Admin Purchase</div>
                <div>
                  This number will be added to the system and can be assigned to any user or Parrot.
                </div>
              </div>
            </div>
          )}

          {/* Terms and Conditions */}
          <div className="text-xs text-gray-500 mb-6">
            By purchasing this phone number, you agree to our{' '}
            <a href="/terms" className="text-teal-600 hover:text-teal-700 underline">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" className="text-teal-600 hover:text-teal-700 underline">
              Privacy Policy
            </a>
            . Monthly charges will be billed to your account automatically.
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex space-x-3 dark:border-gray-700">
          <button
            onClick={onCancel}
            disabled={isPurchasing}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed font-medium transition-colors dark:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmPurchase}
            disabled={isPurchasing || !validationResult?.canPurchase}
            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 ${
              validationResult?.canPurchase 
                ? 'bg-teal-600 text-white hover:bg-teal-700 disabled:bg-gray-400' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isPurchasing ? (
              <>
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Processing...</span>
              </>
            ) : !validationResult?.canPurchase ? (
              <span>Cannot Purchase</span>
            ) : (
              <span>
                {costInfo.isIncludedInPlan || costInfo.total === 0 
                  ? 'Confirm Purchase' 
                  : `Purchase for $${costInfo.total.toFixed(2)}`
                }
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PurchaseConfirmationEnhanced;
